// <LocationFilter> — Home View Step 6 (T-045, plan §4.1 / spec §9).
//
// Page-level location filter for the Home view. Single trigger button
// at the top-right of the page; portal-rendered dropdown panel listing
// "All Locations" + each available site. The selected value drives
// `<HomeView>`'s `useQuery` so the API recomputes Availability, New
// hires, and Anniversaries for the chosen office. Birthdays ignore the
// filter (DOB isn't location-bound; spec §9 explicitly excludes them).

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Globe, ChevronDown } from 'lucide-react';

export interface LocationOption {
  id: string;
  name: string;
}

export interface LocationFilterProps {
  options: LocationOption[];
  /** `null` = "All Locations". */
  activeLocationId: string | null;
  onChange: (next: string | null) => void;
}

const ALL_LOCATIONS_LABEL = 'All Locations';

export function LocationFilter({
  options,
  activeLocationId,
  onChange,
}: LocationFilterProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const panelId = useId();

  const activeLabel =
    options.find((o) => o.id === activeLocationId)?.name ?? ALL_LOCATIONS_LABEL;

  // Close on outside click, Esc.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node | null;
      if (!t) return;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Compute panel position from the trigger's bounding rect — keeps the
  // panel right-aligned to the trigger so it doesn't fall off-screen on
  // the narrow side of the page (spec §9.3).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 6, // 6 px gap below the trigger (spec §9.3 mount animation y-shift)
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Two-phase mount/unmount so the 150 ms fade + 6 px y-shift animation
  // (spec §9.3) actually runs: mount the DOM with opacity 0 / shifted,
  // then on the next frame flip a `mounted` flag so the panel
  // transitions in. On close, flip `mounted` to false first and remove
  // the DOM after the animation completes.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (!open) {
      setMounted(false);
      return;
    }
    // Wait one frame so the initial styles can paint before we toggle
    // to the open styles, so the transition has something to interpolate.
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid="home-location-filter-trigger"
        className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-100"
      >
        <Globe size={14} aria-hidden />
        <span>{activeLabel}</span>
        <ChevronDown
          size={14}
          aria-hidden
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && position && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="menu"
              data-testid="home-location-filter-panel"
              data-mounted={mounted}
              style={{
                position: 'fixed',
                top: position.top,
                right: position.right,
                zIndex: 9999,
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(-6px)',
                transition: 'opacity 150ms ease, transform 150ms ease',
              }}
              className="w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-2"
            >
              <FilterOption
                label={ALL_LOCATIONS_LABEL}
                selected={activeLocationId === null}
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                testId="home-location-filter-option-all"
              />
              {options.map((opt) => (
                <FilterOption
                  key={opt.id}
                  label={opt.name}
                  selected={activeLocationId === opt.id}
                  onClick={() => {
                    onChange(opt.id);
                    setOpen(false);
                  }}
                  testId={`home-location-filter-option-${opt.id}`}
                />
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function FilterOption({
  label,
  selected,
  onClick,
  testId,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      data-testid={testId}
      data-selected={selected}
      className={`w-full text-left px-4 py-2 text-sm font-semibold transition-colors ${
        selected
          ? 'text-[#6344C6] bg-[#6344C6]/5'
          : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
}
